import { IconButton, Icons } from '@commenda/kakashi';
import { cn, formatNumberWithUniqueCommaPattern } from '@commenda/utils';
import type { ReactNode } from 'react';

export interface SelectionActionBarProps {
  /** Number of selected items. The bar renders nothing when <= 0. */
  count: number;
  /** Clears the current selection (wired to the X button). */
  onClear: () => void;
  /** Action buttons (Download, Move, Trash, …) rendered between divider and clear. */
  children: ReactNode;
  /** Singular noun for the count label. Default "item". */
  itemNoun?: string;
  /** Plural override. Default `${itemNoun}s`. */
  itemNounPlural?: string;
  /** Extra classes for the inner container. */
  className?: string;
}

/**
 * Floating, bottom-center bar shown while items are selected. Owns the shell —
 * positioning, container, count label, divider, and the clear button — and
 * renders caller-supplied action buttons as children. Purely presentational:
 * it holds no selection state.
 */
export function SelectionActionBar({
  count,
  onClear,
  children,
  itemNoun = 'item',
  itemNounPlural,
  className,
}: SelectionActionBarProps) {
  if (count <= 0) return null;

  const noun = count === 1 ? itemNoun : (itemNounPlural ?? `${itemNoun}s`);

  return (
    <div className="fixed bottom-12 left-1/2 z-50 -translate-x-1/2">
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border border-border-secondary bg-bg-primary px-4 py-2.5 shadow-lg',
          className
        )}
      >
        <span className="text-sm font-semibold text-text-primary">
          {formatNumberWithUniqueCommaPattern(count)} {noun} selected
        </span>

        <div className="h-5 w-px bg-border-secondary" />

        {children}

        <IconButton
          size="sm"
          variant="default"
          onClick={onClear}
          className="ml-1"
          icon={<Icons.XClose className="size-4 text-fg-quaternary" />}
          aria-label="Clear selection"
        />
      </div>
    </div>
  );
}
